import React, { useEffect, useState } from 'react';
import DOMPurify from 'isomorphic-dompurify';
import { apiHandler } from '../../api/apihandler';
import { CMS_DETAILS } from '../../api/constants';
import PeopleYouMayKnowWidget from './PeopleYouMayKnowWidget';
import MyStatsWidget from './MyStatsWidget';
import '../../views/dashboard/social/social-list-page.css';
import './static-page.css';

// Some CMS records don't hold their content the normal way (a
// cms_page_body section) - e.g. privacy-policy has the real, full policy
// HTML-entity-escaped into the page-level `description` field instead
// (likely pasted in from an external policy generator), while its
// cms_page_body still has an old placeholder section. A textarea round
// trip decodes those entities (browsers resolve character references in
// RCDATA content without parsing embedded "<...>" as tags) so the escaped
// markup becomes real HTML again, safe to hand to DOMPurify below.
const decodeHtmlEntities = (html) => {
  if (!html) return '';
  const el = document.createElement('textarea');
  el.innerHTML = html;
  return el.value;
};

const plainTextLength = (html) => (html || '').replace(/<[^>]*>/g, '').trim().length;

// Below this, a page-level description is just a short placeholder
// ("Terms of Use") rather than real body content, so it shouldn't win
// over an actual cms_page_body section.
const SUBSTANTIAL_DESCRIPTION_LENGTH = 200;

// Shared nemonto-themed shell for the CMS-backed info pages (About Us,
// Privacy Policy, Terms of Use) - same CMS_DETAILS(slug) fetch + body
// rendering the old Bootstrap versions used. Follows the same two-column
// "home screen" layout (.nmsl-layout main + right rail with the People You
// May Know / Your Activity widgets) as the other nemonto dashboard pages -
// the Sidebar nav is the way back, no in-page back button.
const StaticCmsPage = ({ slug, title, subtitle }) => {
  const [sections, setSections] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      setLoading(true);
      setError(false);
      try {
        const res = await apiHandler(CMS_DETAILS, 'POST', { slug });
        if (!isMounted) return;
        if (res?.data?.response?.status === 200) {
          const cmsPage = res?.data?.data?.cmsPage;
          const body = cmsPage?.cms_page_body || [];
          // serial_no drives editorial ordering on the CMS side - sort so a
          // page with multiple sections renders in the intended order.
          const ordered = [...body].sort((a, b) => (a?.serial_no ?? 0) - (b?.serial_no ?? 0));

          const topDescription = decodeHtmlEntities(cmsPage?.description || '');
          if (plainTextLength(topDescription) > SUBSTANTIAL_DESCRIPTION_LENGTH) {
            setSections([{ id: 'page-description', description: topDescription }]);
          } else {
            setSections(ordered);
          }

          if (cmsPage?.meta_title) {
            document.title = cmsPage.meta_title;
          }
        } else {
          setError(true);
        }
      } catch (e) {
        console.error(e);
        if (isMounted) setError(true);
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
    return () => { isMounted = false; };
  }, [slug]);

  return (
    <div className="nmsl-layout">
      <div className="nmsl-main">
        <div className="nmsl-page-header">
          <div className="nmsl-header">
            <h3 className="nmsl-title">{title}</h3>
          </div>
          {subtitle && <p className="nmsl-subtitle">{subtitle}</p>}
        </div>

        <div className="nmsl-main-body">
          <div className="nmsp-card">
            {loading ? (
              <p className="nmsl-loading">Loading…</p>
            ) : error ? (
              <p className="nmsl-empty-note">We couldn't load this page right now. Please try again later.</p>
            ) : sections && sections.length > 0 ? (
              sections.map((elm, i) => (
                <section key={elm?.id || i} className="nmsp-section">
                  {elm?.heading && <h2>{elm.heading}</h2>}
                  {elm?.sub_heading && elm.sub_heading !== elm.heading && (
                    <p className="nmsp-sub-heading">{elm.sub_heading}</p>
                  )}
                  {elm?.image && (
                    <img className="nmsp-image" src={elm.image} alt={elm?.heading || title} />
                  )}
                  <div className="nmsp-body" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(elm?.description || '') }} />
                  {elm?.button_text && elm?.button_link && (
                    <a className="nmsp-cta" href={elm.button_link} target="_blank" rel="noopener noreferrer">
                      {elm.button_text}
                    </a>
                  )}
                </section>
              ))
            ) : (
              <p className="nmsl-empty-note">Nothing to show here yet.</p>
            )}
          </div>
        </div>
      </div>

      <aside className="nmsl-rail">
        <PeopleYouMayKnowWidget />
        <MyStatsWidget />
      </aside>
    </div>
  );
};

export default StaticCmsPage;
